/**
 * Local settings (`03-client-app.md` section 4.2, `/settings`).
 *
 * These live on the device, not on the server: they describe how this install behaves, not who
 * the user is. Notification mutes are the exception and are stored by the API, so turning a type
 * off on the desktop also stops the push to the mobile app.
 */

import * as React from 'react';
import type { GlyphSet } from '../gamepad/glyphs.js';
import type { BigPictureLaunch } from './bigPicture.js';

export interface Settings {
  /** `auto` follows the controller, anything else forces a glyph set. */
  glyphOverride: GlyphSet | 'auto';
  /** Bytes per second, or null for no limit. */
  downloadLimitBps: number | null;
  /** Between 4 and 32. */
  downloadConcurrency: number;
  autoUpdate: boolean;
  /**
   * Whether closing the window leaves the app in the tray rather than ending it.
   *
   * On by default: the download queue and the app's own update check run in that process and
   * nowhere else, so a close that ends it is a close that stops a 30 GB download. Ignored on a
   * desktop with no tray, where the shell closes the window either way.
   */
  closeToTray: boolean;
  /** Keeps the assembled APK so the next Android update is a delta. */
  keepAndroidApk: boolean;
  /** Notification types the user turned off, mirrored to the API. */
  mutedNotifications: string[];
  /**
   * Drops every transform and animation, on top of whatever the platform already asks for.
   *
   * The brand guide makes this a first-class control rather than an accessibility afterthought:
   * the outline and the state labels stay, only the movement goes.
   */
  reduceMotion: boolean;
  /**
   * Whether the screen's artwork is drawn behind the whole app.
   *
   * On by default, because it is most of what makes this read as a console. Off is for a
   * handheld, where a full-screen blur is work the GPU is doing for the room rather than for
   * the game: the shipped wallpaper stays, the game art stops being loaded for it.
   */
  ambientArt: boolean;
  /**
   * Whether the app opens in Big Picture (`03-client-app.md` section 4.6).
   *
   * `auto` is on for Linux and off for Windows: Linux is where a Steam Deck runs this from
   * Gaming Mode, with no desktop behind the window and no input but a controller. `on` and
   * `off` are somebody overruling that, and they take effect the moment they are set rather
   * than at the next launch — a preference that only applies tomorrow is one nobody can tell
   * they changed.
   */
  bigPictureOnLaunch: BigPictureLaunch;
  /**
   * Whether this install reports how it is used.
   *
   * On by default, and worth being plain about why that is defensible: what it sends carries no
   * identifier of any kind, goes nowhere but our own Cloudflare account, and is the only reason
   * we can tell that a game is being installed and then never launched. Off stops it at the
   * source — nothing is queued, so there is nothing to send later either.
   */
  shareUsageData: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  glyphOverride: 'auto',
  downloadLimitBps: null,
  downloadConcurrency: 16,
  autoUpdate: true,
  closeToTray: true,
  keepAndroidApk: true,
  mutedNotifications: [],
  reduceMotion: false,
  ambientArt: true,
  bigPictureOnLaunch: 'auto',
  shareUsageData: true,
};

const STORAGE_KEY = 'gb.settings.v1';

function read(): Settings {
  if (typeof localStorage === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function write(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // A full or blocked storage must not break the app.
  }
}

type Listener = (settings: Settings) => void;
const listeners = new Set<Listener>();
let current = read();

/** Mirrors the motion preference onto the document, which is where the stylesheet reads it. */
function applyMotion(settings: Settings): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.reduceMotion = String(settings.reduceMotion);
}

applyMotion(current);

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings>): Settings {
  current = { ...current, ...patch };
  write(current);
  applyMotion(current);
  for (const listener of listeners) listener(current);
  return current;
}

/**
 * Subscribes to every settings change.
 *
 * `useSettings` is the hook a screen wants; this is for the parts of the app that are not
 * components and still have to follow a setting, like the window taking the screen.
 */
export function onSettingsChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSettings(): Settings & { update: (patch: Partial<Settings>) => void } {
  const [settings, setSettings] = React.useState(current);
  React.useEffect(() => {
    listeners.add(setSettings);
    return () => {
      listeners.delete(setSettings);
    };
  }, []);
  return { ...settings, update: updateSettings };
}

/* ----------------------------------------------------- affiliate cookie */

/** Days the referral code from `?a=` is remembered. */
export const AFFILIATE_TTL_DAYS = 30;
const AFFILIATE_KEY = 'gb_aff';

/**
 * Stores the referral code so a visitor who comes back within a month still credits the creator
 * who sent them (`01-product-rules.md` section 6).
 */
export function captureAffiliate(search: string): void {
  const code = new URLSearchParams(search).get('a');
  if (!code) return;
  const safe = code.slice(0, 64).replace(/[^a-zA-Z0-9_.-]/g, '');
  if (!safe) return;
  const expires = new Date(Date.now() + AFFILIATE_TTL_DAYS * 24 * 60 * 60 * 1000).toUTCString();
  try {
    document.cookie = `${AFFILIATE_KEY}=${encodeURIComponent(safe)}; expires=${expires}; path=/; SameSite=Lax`;
    localStorage.setItem(
      AFFILIATE_KEY,
      JSON.stringify({ code: safe, expires: Date.now() + AFFILIATE_TTL_DAYS * 86400000 }),
    );
  } catch {
    // Cookies blocked: the code is simply not remembered.
  }
}

export function readAffiliate(): string | null {
  try {
    const raw = localStorage.getItem(AFFILIATE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as { code: string; expires: number };
      if (stored.expires > Date.now()) return stored.code;
      localStorage.removeItem(AFFILIATE_KEY);
    }
  } catch {
    // Fall through to the cookie.
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${AFFILIATE_KEY}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
